import { apiFetch } from './apiFetch';
import { ensureCsrfToken } from './publicApi';

const API_BASE = process.env.REACT_APP_API_URL;

export async function createSubscription(planType) {
  await ensureCsrfToken();
  const res = await apiFetch(`${API_BASE}/subscription`, {
    method: 'POST',
    body: JSON.stringify({ planType })
  });
  return res.json();
}

export async function cancelCreateSubscription() {
  await ensureCsrfToken();
  const res = await apiFetch(`${API_BASE}/subscription/cancel-create`, { method: 'POST' });
  return res.json();
}

export async function verifySignature(paymentId, signature) {
  const res = await apiFetch(`${API_BASE}/subscription/verify-signature`, {
    method: 'POST',
    body: JSON.stringify({ razorpay_payment_id: paymentId, razorpay_signature: signature })
  });
  return res.json();
}

export async function getSubscription() {
  const res = await apiFetch(`${API_BASE}/subscription`);
  return res.json();
}

export async function cancelSubscription(password) {
  const res = await apiFetch(`${API_BASE}/subscription/cancel`, {
    method: 'POST',
    body: JSON.stringify({ password })
  });
  return res.json();
}

export async function upgradeSubscription() {
  const res = await apiFetch(`${API_BASE}/subscription/upgrade`, { method: 'POST' });
  return res.json();
}

export async function assignSubscriptionToUser(subscriptionId) {
  const res = await apiFetch(`${API_BASE}/subscription/${subscriptionId}/assign`, { method: 'POST' });
  return res.json();
}

export async function fetchLicense() {
  const res = await apiFetch(`${API_BASE}/subscription/license`);
  return res.json();
}

// Where /subscription-check sends an active subscriber once it has confirmed them (EL-49).
//
// It used to be '/dashboard', always. DashboardPage sends any session without
// sessionStorage.isSubscribed through the check - and a NEW TAB never has that flag, because
// App.js's session restore rebuilds the login from the cookie but deliberately does not
// assume the subscription. So an alert-reminder email's /dashboard?id=42, which opens in a
// new tab almost by definition, went /dashboard?id=42 -> /subscription-check -> /dashboard:
// the check passed and the record the email was about never opened. The dashboard now says
// where it was headed, and this is the one place that decides whether to honour it.
//
// Only a dashboard address is honoured. The value arrives in router state rather than the
// URL, so it is not something a link can set, but "send the user anywhere" is not a
// capability this page needs - it only ever interrupts one page.
export function postCheckDestination(state) {
  const returnTo = state?.returnTo;
  if (typeof returnTo === 'string' && /^\/dashboard(\?|$)/.test(returnTo)) {
    return returnTo;
  }
  return '/dashboard';
}

// Classifies a GET /subscription response for SubscriptionCheckPage.js, extracted so the
// branching is unit-testable (no component-render harness exists in this project).
//
// 'not_subscribed' (-> /subscribe) must stay reserved for the one case that actually means
// that: a 404 SUBSCRIPTION_NOT_FOUND with no voucher key to redeem, i.e. the user has never
// had a subscriptionId assigned at all. GET /subscription's status also depends on a *live*
// Razorpay lookup (paymentUtils.fetchSubscription) on every call, with no caching - any
// transient failure there (timeout, rate limit, a blip right as a user reconnects after a
// session timeout) surfaces as a generic success:false/500, not SUBSCRIPTION_NOT_FOUND.
// Treating every failure as 'not_subscribed' told real, active subscribers "you have no
// subscription" and shoved them onto the buy-a-plan page over a technical hiccup that had
// nothing to do with their actual subscription status - that failure mode now classifies as
// 'error' instead, so the caller can show a retryable message rather than a wrong redirect.
export function classifySubscriptionCheckResult(res, subscriptionKey) {
  const needsAssign = (res.success && res.subscription.status !== 'active' && subscriptionKey && res.subscription.subscriptionId !== subscriptionKey)
    || (!res.success && res.code === 'SUBSCRIPTION_NOT_FOUND' && subscriptionKey);

  if (needsAssign) {
    return { type: 'assign' };
  }
  if (res.success) {
    return { type: 'status', status: res.subscription.status, paymentLink: res.subscription.short_url || null };
  }
  if (res.code === 'SUBSCRIPTION_NOT_FOUND') {
    return { type: 'not_subscribed' };
  }
  return { type: 'error' };
}

export function getPlanDescriptions() {
  return {
    FREE: {
      price: '₹0',
      features: ['Up to 10 records', 'No file attachments', 'No alert reminders', 'No nominee access']
    },
    SILVER: {
      price: '₹999/year',
      features: ['Up to 100 records', 'Alert reminders', '1 nominee', 'No file attachments']
    },
    GOLD: {
      price: '₹1499/year',
      features: ['Up to 1,000 records', '2GB storage', '2 files per record (2MB each)', '3 nominees', 'Alert reminders']
    },
    PLATINUM: {
      price: '₹2499/year',
      features: ['Unlimited records', '5GB storage', '4 files per record (5MB each)', 'Unlimited nominees', 'Alert reminders']
    }
  };
}
